import React, { useEffect, useState } from 'react';
import { Button, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import { Edit, HighlightOffRounded } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { IFile } from '../../api-client/autogenerated';
import { getFileById, getLatestSubmissionFileByDocumentId } from '../../models/api/files';
import { getUserState } from '../../features/user/selectors';
import { descendingComparator } from '../document-index/DocumentIndexUtils';

const useStyles = makeStyles({
  textfield: {
    height: '32px',

    /* Gray / Gray 50 */
    background: '#F9F9F9',

    /* Gray / Gray 400 Brand dark */
    border: '1px solid #949494',
    borderRadius: '5px',
  },
});

type Props = {
  documentId: string;
  /** Posted filename for the version the markups will be flattened into, without its extension. */
  filename: string;
  onFilenameChange: (filename: string) => void;
  /** Reports whether the .bax Bluebeam writes back has finished uploading. */
  onReadyChange: (isReady: boolean) => void;
  /** Discards the pending markup -- the review is then submitted without a Bluebeam version. */
  onRemove: () => void;
};

/**
 * Names the version CENTERLINE creates from Bluebeam markups, inline in the action center: the
 * architect launches Bluebeam, marks the file up, and comes back to a field already filled in with
 * the suggested name. The field stays read-only until EDIT is pressed, so the suggested name is
 * the path of least resistance, and the version itself is posted when the review is submitted.
 *
 * Bluebeam writes the markups back as a .bax file, which has to finish uploading before the
 * flatten can run -- this polls for that and reports it upward, so the form can hold the submit
 * until the markups are actually here.
 */
export default function BluebeamFileField(props: Props) {
  const classes = useStyles();
  const { documentId, filename, onFilenameChange, onReadyChange, onRemove } = props;

  const user = useSelector(getUserState);

  const [isEditing, setIsEditing] = useState(false);
  const [baxFile, setBaxFile] = useState<IFile>();

  const isReady = !!baxFile?.isVerified;

  // The .bax is written by Bluebeam once the user saves, so the parent file is re-read here
  // rather than relying on the copy the action center already holds.
  useEffect(() => {
    let cancelled = false;
    getLatestSubmissionFileByDocumentId(documentId).then((latestFile) => {
      if (cancelled || !latestFile) return;
      const bax = latestFile.childBaxFiles
        ?.filter((c) => c.name === `${user.id || sessionStorage.getItem('publicUserId')}.bax`)
        ?.sort((a, b) => descendingComparator(a, b, 'createdOn'))[0];
      if (bax) setBaxFile(bax);
    });
    return () => {
      cancelled = true;
    };
  }, [documentId, user.id]);

  useEffect(() => {
    if (!baxFile || baxFile.isVerified) return;

    const interval = setInterval(async () => {
      const updatedBax = await getFileById(baxFile.id);
      if (updatedBax.isVerified) {
        setBaxFile(updatedBax);
        clearInterval(interval);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [baxFile]);

  useEffect(() => {
    onReadyChange(isReady);
  }, [isReady]);

  return (
    <>
      <Typography style={{ marginTop: '8px' }}>Markup from Bluebeam:</Typography>
      <div style={{ display: 'flex', paddingTop: 8, alignItems: 'center' }}>
        <IconButton onClick={onRemove} style={{ padding: 0, marginRight: 12 }}>
          <HighlightOffRounded htmlColor="#616061" />
        </IconButton>
        <TextField
          fullWidth
          value={filename}
          disabled={!isEditing}
          onChange={(event) => onFilenameChange(event.target.value)}
          InputProps={{
            fullWidth: true,
            disableUnderline: true,
            className: classes.textfield,
            endAdornment: <span>.pdf</span>,
            style: { padding: '0px 4px 0px 4px' },
          }}
        />
        {!isEditing ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setIsEditing(true)}
            startIcon={<Edit fontSize="small" />}
            style={{ marginLeft: 8, color: '#7A797A', borderColor: '#949494' }}
          >
            EDIT
          </Button>
        ) : null}
      </div>
      {!isReady ? (
        <Typography variant="subtitle1" style={{ marginTop: 4, textTransform: 'none' }}>
          Waiting for your markups to finish uploading from Bluebeam...
        </Typography>
      ) : null}
    </>
  );
}
